// First-load location auto-detect (#85). Tries navigator.geolocation
// first; on denial / error / timeout, falls back to /api/geo (CloudFront
// viewer headers). Whichever produces lat/lon is reverse-geocoded via
// /api/location/nearest to a real city name.
//
// Sticky: once attempted the `autoLocationTried` localStorage flag
// prevents re-running on later visits — the existing Location icon in
// AppHeader is the path back in for an override.

import httpClient from '../utils/httpClient'
import { apiConfig } from '../utils/apiConfig'
import { getCurrentPosition } from '../utils/geo'

const TRIED_KEY = 'autoLocationTried'
// City > town > village > metropolitan: prefer the most human-readable
// label that still corresponds to where the user actually is.
const NEAREST_LEVEL_PRIORITY = ['city', 'town', 'village', 'metropolitan']

type SetLocationFn = (location: string, countryCode?: string) => void

async function fetchNearestCity(lat: number, lon: number): Promise<{ name: string; countryCode: string } | null> {
  try {
    const resp = await httpClient.get(apiConfig.buildUrl(`/api/location/nearest?lat=${lat}&lon=${lon}`))
    const locs = resp?.data?.locations
    if (!locs) return null
    for (const level of NEAREST_LEVEL_PRIORITY) {
      const item = locs[level]
      if (item?.location_name) {
        return { name: item.location_name, countryCode: item.country_code || '' }
      }
    }
  } catch {
    // Network / 5xx — caller treats as failed
  }
  return null
}

async function ipGeo(): Promise<{ lat: number; lon: number } | null> {
  try {
    const resp = await httpClient.get(apiConfig.buildUrl('/api/geo'))
    const lat = resp?.data?.lat
    const lon = resp?.data?.lon
    if (typeof lat === 'number' && typeof lon === 'number') {
      return { lat, lon }
    }
  } catch {
    // CloudFront headers absent (origin-request-policy not yet switched)
    // or network failure — caller falls through to chip prompt.
  }
  return null
}

export async function autoDetectLocation(setLocation: SetLocationFn, currentLocation: string): Promise<void> {
  if (currentLocation.trim()) return
  if (localStorage.getItem(TRIED_KEY) === '1') return
  localStorage.setItem(TRIED_KEY, '1')

  let lat: number | undefined
  let lon: number | undefined

  try {
    const pos = await getCurrentPosition({
      enableHighAccuracy: false,
      timeout: 8000,
      maximumAge: 300000,
    }) as GeolocationPosition
    lat = pos.coords.latitude
    lon = pos.coords.longitude
  } catch {
    const ip = await ipGeo()
    if (ip) {
      lat = ip.lat
      lon = ip.lon
    }
  }

  if (lat === undefined || lon === undefined) return

  const nearest = await fetchNearestCity(lat, lon)
  if (nearest) setLocation(nearest.name, nearest.countryCode)
}
